import themeJSON from "../../../../src/variants/themes/default.json";

export type PlantMode = "light" | "dark";

export type PlantDensity = "compact" | "spacious";

export type PlantSharpness = "sharp" | "soft" | "rounded";

export type PlantDevice = "phone" | "monitor";

export type PlantState = {
    sharpness: PlantSharpness;
    density: PlantDensity;
    palette: number;
    mode: PlantMode;
};

export type ScreenColors = {
    borderSubtle: string;
    onPrimary: string;
    textHigh: string;
    pressed: string;
    primary: string;
    textLow: string;
    border: string;
    subtle: string;
    raised: string;
    focus: string;
    hover: string;
    page: string;
};

export type ScreenTokens = {
    controlPadding: number;
    controlHeight: number;
    colors: ScreenColors;
    cardPadding: number;
    smallHeight: number;
    actions: number;
    surface: number;
    form: number;
    chip: number;
    gap: number;
};

export const PALETTE_NAMES = themeJSON.palettes.map((palette) => palette.name);

export const MODES: Array<PlantMode> = [
    "light",
    "dark"
];

export const DENSITIES: Array<PlantDensity> = [
    "compact",
    "spacious"
];

export const SHARPNESSES: Array<PlantSharpness> = [
    "sharp",
    "soft",
    "rounded"
];

export const REST_STATE: PlantState = {
    density: "spacious",
    sharpness: "soft",
    mode: "light",
    palette: 0
};

const shapes = themeJSON.shapes;

/**
 * A coarse pointer never gets the compact density: the kit's theme provider turns
 * "compact" into "spacious" on touch screens, so touch targets stay large enough.
 */
export const densityOnDevice = (device: PlantDevice, density: PlantDensity): PlantDensity => device === "phone" ? "spacious" : density;

export const primaryOf = (palette: number, mode: PlantMode) => themeJSON.palettes[palette]!.themes[mode].system.default.primary;

export const colorsOf = (palette: number, mode: PlantMode): ScreenColors => {
    const theme = themeJSON.palettes[palette]!.themes[mode];

    const {
        container,
        border,
        text
    } = theme.content;

    const overlay = theme.system.state.overlay;

    return {
        primary: theme.system.default.primary,
        pressed: overlay.pressed.primary,
        focus: overlay.focus.default,
        hover: overlay.hover.default,
        borderSubtle: border.subtle,
        onPrimary: text.onPrimary,
        raised: container.raised,
        subtle: container.subtle,
        page: container.default,
        border: border.default,
        textHigh: text.high,
        textLow: text.low
    };
};

export const screenTokensOf = (state: PlantState, device: PlantDevice, palette: number): ScreenTokens => {
    const density = densityOnDevice(device, state.density);

    const radiuses = shapes.radiuses[state.sharpness];

    return {
        controlPadding: shapes.controlPaddings[density].medium,
        controlHeight: shapes.controlHeights[density].medium,
        smallHeight: shapes.controlHeights[density].small,
        cardPadding: shapes.spaces[density].spacingMd,
        colors: colorsOf(palette, state.mode),
        gap: shapes.spaces[density].spacingSm,
        actions: radiuses.actions,
        surface: radiuses.surface,
        chip: radiuses.chip,
        form: radiuses.form
    };
};

export const controlHeightOf = (density: PlantDensity) => shapes.controlHeights[density].medium;

export const FOCUS_WIDTH = shapes.borders.focus;

export const readoutOf = (state: PlantState) => `${PALETTE_NAMES[state.palette]} · ${state.mode} · ${state.density} · ${state.sharpness} · ${primaryOf(state.palette, state.mode)}`;
